// ... other imports remain unchanged
import React, { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import {
  handleAccept,
  handleClose,
  handlePause,
  handleReactivate,
  handleReopen,
} from "../../../utils/Incident/incidentHandlers";
import { IncidentData } from "../../../utils/Incident/incidentTypes";
import "../Modal.css";
import ConfirmCloseModal from "./ConfirmClose";
import ForwardModal from "./ForwardModal";
import JournalModal from "./JournalModal";
import CommunicationModal from "./CommunicationModal";
import { authFetch } from "../../../utils/Security/authFetch";
import { getFullNameByUsername } from "../../../utils/durationUtils";
const isAdmin = localStorage.getItem("isAdmin") === "true";

type Message = {
  id: number;
  username: string;
  message: string;
  date: string;
};

type Communication = {
  id: number;
  users: string;
  message: string;
  date: string;
};

interface ModalProps {
  isOpen: boolean;
  data: any;
  onClose: () => void;
  onUpdate?: (updatedIncident: IncidentData) => void; // ✅ new
}

const hiddenKeys = [
  "isaccepted",
  "isFavorite",
  "isallsite",
  "isclosed",
  "foruserid",
  "isforwarded",
  "isreopened",
  "isreactivated",
];

const ticketInfo = [
  "createdby",
  "userid",
  "ticketId",
  "status",
  "ticketCreationDatetime",
  "ticketClosureDatetime",
  "incidentStartDatetime",
  "incidentCloseDatetime",
  "customerComplaint",
  "customerInformed",
  "incidentCrossReference",
];
const customerInfo = [
  "affectedCustomer",
  "affectedNetwork",
  "affectedSite",
  "affectedLink",
  "asset",
];
const slaInfo = [
  "reactionPoint",
  "",
  "sla",
  "slaStatus",
  "ola",
  "olaStatus",
  "acceptanceTime",
  "acceptanceStatus",
  "responseTimeToOpen",
  "responseTimeToClose",
];
const statusInfo = [
  "summary",
  "description",
  "solution",
  "closureCode",
  "errorType",
  "category",
];
const resInfo = ["responsibleTeam", "responsiblePerson"];
const incInfo = ["urgency", "impact", "priority"];
const relInfo = ["filePath", "relatedobject"];

const fieldLabels: { [key: string]: string } = {
  // Ticket Info
  createdby: "Created By",
  user: "User",
  userid: "User",
  ticketId: "Ticket ID",
  status: "Status",
  ticketCreationDatetime: "Ticket Creation Date/Time",
  ticketClosureDatetime: "Ticket Closure Date/Time",
  incidentStartDatetime: "Incident Start Date/Time",
  incidentCloseDatetime: "Incident Close Date/Time",
  customerComplaint: "Customer Complaint",
  customerInformed: "Customer Informed",
  incidentCrossReference: "Incident Cross Reference",
  category: "Category",
  // Customer & Network
  affectedCustomer: "Customer",
  affectedNetwork: "Network",
  affectedSite: "Site",
  affectedLink: "Link",
  asset: "Asset",

  // SLA & OLA
  reactionPoint: "Reaction Point",
  sla: "SLA",
  slaStatus: "SLA Status",
  ola: "OLA",
  olaStatus: "OLA Status",
  acceptanceTime: "Acceptance Time",
  acceptanceStatus: "Acceptance Status",
  responseTimeToOpen: "Response Time To Open",
  responseTimeToClose: "Response Time To Close",

  // Status & Description
  summary: "Summary",
  description: "Description",
  solution: "Solution",
  closureCode: "Closure Code",
  errorType: "Error Type",

  // Responsibility
  responsiblePerson: "Responsible Person",
  responsibleTeam: "Responsible Team",
  relatedobject: "Related Object",

  // Severity
  urgency: "Urgency",
  impact: "Impact",
  priority: "Priority",

  // Related
  filePath: "File Attachment",
};
const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  return dateStr.replace("T", " ").split(".")[0];
};

const buttonStyle = {
  display: "block",
  marginTop: 5,
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "6px 12px",
  cursor: "pointer",
  borderRadius: 4,
};

const Modal: React.FC<ModalProps> = ({ isOpen, data, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [selectedIncident, setSelectedIncident] = useState<IncidentData | null>(
    null,
  );
  const [journalMessages, setJournalMessages] = useState<Message[]>([]);
  const [communicationMessages, setCommunicationMessages] = useState<
    Communication[]
  >([]);
  const [journalTitle, setJournalTitle] = useState("Add Journal");
  const [fileImages, setFileImages] = useState<{
    [path: string]: string | null;
  }>({});
  const [fullNames, setFullNames] = useState<Record<string, string>>({});

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showCommunication, setShowCommunication] = useState(false);
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [pauseAfterJournal, setPauseAfterJournal] = useState(false); // 🟢 new state

  function renderSafeMessage(html: string): React.ReactNode {
    // Fast path: no tags
    if (!html?.includes("<")) return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const nodes: React.ReactNode[] = [];
    let key = 0;

    function walk(node: Node) {
      // Text
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? "";
        if (text.trim()) nodes.push(<span key={key++}>{text}</span>);
        return;
      }

      // Element
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();

        // Allow only IMG with base64 data url
        if (tag === "img") {
          const src = (el as HTMLImageElement).getAttribute("src") || "";

          const isAllowed =
            src.startsWith("data:image/") && src.includes(";base64,");

          if (isAllowed) {
            nodes.push(
              <img
                key={key++}
                src={src}
                alt="embedded"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                  marginTop: 8,
                  borderRadius: 8,
                }}
              />,
            );
          }
          return;
        }

        // Add line breaks for <br>, <p>, <div>, <li> etc.
        if (tag === "br") {
          nodes.push(<br key={key++} />);
          return;
        }

        // Walk children for all other tags, but DON'T render the tag itself
        Array.from(el.childNodes).forEach(walk);

        // Optional: add spacing after block-like tags
        if (["p", "div", "li"].includes(tag)) {
          nodes.push(<br key={key++} />);
        }
      }
    }

    Array.from(doc.body.childNodes).forEach(walk);

    // If nothing collected, fallback to textContent
    if (nodes.length === 0) return doc.body.textContent || "";

    return <>{nodes}</>;
  }

  const [username, setUsername] = useState<string>("");
  useEffect(() => {
    console.log("Fetching full name for:", username);

    const loadNames = async () => {
      const usernames = [
        ...journalMessages.map((m) => m.username),
        ...communicationMessages.map((m) => m.users),
      ];

      for (const username of usernames) {
        if (!username) continue;

        if (!fullNames[username]) {
          const name = await getFullNameByUsername(username);
          setFullNames((prev) => ({ ...prev, [username]: name }));
        }
      }
    };

    if (journalMessages.length || communicationMessages.length) {
      loadNames();
    }
  }, [journalMessages, communicationMessages]);
  const stripHtml = (htmlString: string) => {
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    return div.textContent || div.innerText || "";
  };
  const useAuthImage = (
    path: string | null,
    authFetch: any,
    apiBaseUrl: string,
  ) => {
    const [imgUrl, setImgUrl] = useState<string | null>(null);
    const loadFullName = async (username: string) => {
      console.log("nameeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee", username);

      if (!username) return "";

      if (fullNames[username]) return fullNames[username];

      const name = await getFullNameByUsername(username);

      setFullNames((prev) => ({
        ...prev,
        [username]: name,
      }));

      return name;
    };

    useEffect(() => {
      if (!path) return;

      const fetchImage = async () => {
        try {
          const normalizedPath = path.replace(/\\/g, "/");
          const fileName = normalizedPath.split("/").pop();
          if (!fileName) return;

          const res = await authFetch(
            `${apiBaseUrl}/api/incidents/files/${fileName}`,
            { method: "GET" },
          );

          if (!res.ok) throw new Error("Failed to fetch image");

          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setImgUrl(url);

          return () => URL.revokeObjectURL(url);
        } catch (err) {
          console.error("Image fetch error:", err);
        }
      };

      fetchImage();
    }, [path, authFetch, apiBaseUrl]);

    return imgUrl;
  };
  const fetchImages = async () => {
    const paths = Array.isArray(data?.filePath)
      ? data.filePath
      : data?.filePath
        ? [data.filePath]
        : [];
    const newImages: { [path: string]: string | null } = {};
    for (const path of paths) {
      if (!path) continue;
      try {
        const normalizedPath = path.replace(/\\/g, "/");
        const fileName = normalizedPath.split("/").pop();
        if (!fileName) continue;

        const res = await authFetch(
          `${config.apiBaseUrl}/api/incidents/files/${fileName}`,
          { method: "GET" },
        );
        if (!res.ok) throw new Error("Failed to fetch image");

        const blob = await res.blob();
        newImages[path] = URL.createObjectURL(blob);
      } catch (err) {
        console.error("Image fetch error:", err);
        newImages[path] = null;
      }
    }
    setFileImages(newImages);
  };
  const formatMessage = (
    message: string,
    fileImages: Record<string, string | null>,
    downloadFile: (path: string) => void,
  ) => {
    // --- 1️⃣ Handle inline <img src="data:image/..."> inside the message ---
    const imgTagRegex = /<img\s+src="(data:image\/[^"]+)"/gi;
    const hasInlineImages = message.includes('<img src="data:image');

    // --- 2️⃣ Handle file attachments ---
    const fileRegex = /\| Files:\s*(.*)/i;
    const fileMatch = message.match(fileRegex);
    const filesStr = fileMatch ? fileMatch[1] : null;
    const files = filesStr
      ? filesStr
          .split(",")
          .map((f) => f.trim())
          .filter(Boolean)
      : [];

    // --- 3️⃣ Extract text before the files section ---
    const textPart = fileMatch ? message.split("| Files:")[0] : message;

    // --- 4️⃣ Render the message text + inline images ---
    let content: (string | JSX.Element)[] = [];

    if (hasInlineImages) {
      // Split text around <img> tags
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = imgTagRegex.exec(textPart)) !== null) {
        const [fullTag, imgSrc] = match;
        const before = textPart.slice(lastIndex, match.index);
        if (before.trim()) content.push(before);

        content.push(
          <img
            key={match.index}
            src={imgSrc}
            alt="Embedded"
            style={{
              maxWidth: "100%",
              maxHeight: "300px",
              borderRadius: "5px",
              display: "block",
              marginTop: "0.5rem",
              marginBottom: "0.5rem",
            }}
          />,
        );

        lastIndex = match.index + fullTag.length;
      }

      const after = textPart.slice(lastIndex);
      if (after.trim()) content.push(after);
    } else {
      content.push(textPart);
    }

    // --- 5️⃣ Render file attachments (downloadable or images from server) ---
    return (
      <div style={{ wordBreak: "break-word" }}>
        {content}

        {files.length > 0 && (
          <>
            {" "}
            | Files:
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {files.map((path, i) => {
                const normalizedPath = path.replace(/\\/g, "/");
                const fileName =
                  normalizedPath.split("/").pop() || `File${i + 1}`;
                const ext = fileName.split(".").pop()?.toLowerCase();
                const isImage = [
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "webp",
                ].includes(ext || "");

                // fallback to backend URL if not in fileImages
                const imgUrl =
                  fileImages[path] ||
                  `${config.apiBaseUrl}/api/incidents/files/${fileName}`;

                return (
                  <li key={i} style={{ marginBottom: "10px" }}>
                    {isImage ? (
                      <img
                        src={imgUrl}
                        alt={fileName}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "5px",
                          display: "block",
                          marginTop: "0.5rem",
                        }}
                      />
                    ) : (
                      <button
                        style={{
                          color: "blue",
                          textDecoration: "underline",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        onClick={() => downloadFile(path)}
                      >
                        {fileName}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderSection = (title: string, keys: string[], data: any) => (
    <div className="modal-section">
      <h3>{title}</h3>
      <div className="modal-section-content">
        {keys
          .filter(
            (key) =>
              data[key] !== undefined &&
              !hiddenKeys.includes(key) &&
              (isAdmin ||
                ![
                  "reactionPoint",
                  "responseTimeToOpen",
                  "responseTimeToClose",
                  "impact",
                  "priority",
                  "acceptanceTime",
                  "sla",
                  "slaStatus",
                  "relatedobject",
                  "ola",
                  "category",
                  "olaStatus",
                  "incidentCrossReference",
                  "customerComplaint",
                  "customerInformed",
                ].includes(key)),
          )
          .map((key, index) => (
            <div key={index} className="modal-item">
              <strong>{fieldLabels[key] || key}:</strong>
              <div className="modal-value">
                {key === "filePath" ? (
                  Array.isArray(data[key]) ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {data[key].map((path: string | null, i: number) => {
                        if (!path) return null;

                        const normalizedPath = path.replace(/\\/g, "/");
                        const fileName =
                          normalizedPath.split("/").pop() || `File${i + 1}`;
                        const ext = fileName.split(".").pop()?.toLowerCase();
                        const isImage = [
                          "png",
                          "jpg",
                          "jpeg",
                          "gif",
                          "bmp",
                          "webp",
                        ].includes(ext || "");
                        const imgUrl = fileImages[path] || null;

                        return (
                          <li key={i} style={{ marginBottom: "10px" }}>
                            {isImage && imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={fileName}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "300px",
                                  borderRadius: "5px",
                                }}
                              />
                            ) : (
                              <button
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                                onClick={() => downloadFile(path)}
                              >
                                {fileName}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    (() => {
                      const normalizedPath =
                        data[key]?.replace(/\\/g, "/") || "";
                      const fileName =
                        normalizedPath.split("/").pop() || "file";
                      const ext = fileName.split(".").pop()?.toLowerCase();
                      const isImage = [
                        "png",
                        "jpg",
                        "jpeg",
                        "gif",
                        "bmp",
                        "webp",
                      ].includes(ext || "");
                      const fileUrl = `${config.apiBaseUrl}/api/incidents/files/${fileName}`;

                      return isImage ? (
                        <img
                          src={fileUrl}
                          alt={fileName}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "5px",
                          }}
                        />
                      ) : (
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                          onClick={() => downloadFile(data[key])}
                        >
                          {fileName}
                        </button>
                      );
                    })()
                  )
                ) : key === "userid" ? (
                  username
                ) : key === "relatedobject" && data[key] ? (
                  (Array.isArray(data[key])
                    ? data[key]
                    : (data[key] as string).split(",")
                  ).map((item: string, idx: number) => {
                    const ticketId = item.trim();
                    let path = `/incident-edit/${ticketId}`; // default

                    if (ticketId.startsWith("TSK"))
                      path = `/task-edit/${ticketId}`;
                    else if (ticketId.startsWith("CHN"))
                      path = `/change-edit/${ticketId}`;
                    else if (ticketId.startsWith("IND"))
                      path = `/incident-edit/${ticketId}`;

                    return (
                      <React.Fragment key={idx}>
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            marginRight: 5,
                          }}
                          onClick={() => navigate(path, { state: { data } })}
                        >
                          {ticketId}
                        </button>
                        {idx <
                        (Array.isArray(data[key])
                          ? data[key].length
                          : (data[key] as string).split(",").length) -
                          1
                          ? ", "
                          : ""}
                      </React.Fragment>
                    );
                  })
                ) : Array.isArray(data[key]) ? (
                  data[key].join(", ")
                ) : typeof data[key] === "string" ? (
                  ["description", "summary", "solution"].includes(key) ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: data[key] }}
                      style={{ whiteSpace: "normal" }}
                    />
                  ) : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(data[key]) ? (
                    formatDate(data[key])
                  ) : (
                    data[key]
                  )
                ) : (
                  JSON.stringify(data[key], null, 2)
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  const downloadFile = async (path: string) => {
    try {
      if (!path) return;
      const normalizedPath = path.replace(/\\/g, "/");
      const originalFileName = normalizedPath.split("/").pop();
      if (!originalFileName) return;

      const res = await authFetch(
        `${
          config.apiBaseUrl
        }/api/incidents/downloadFile?path=${encodeURIComponent(path)}`,
      );
      if (!res.ok) throw new Error("Failed to download file");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = originalFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  useEffect(() => {
    if (isOpen && data?.ticketId) {
      fetchMessages();
      fetchUsername();
      fetchCommunication();
      fetchUpdatedIncident2();
    }
  }, [isOpen, data]);
  const incidentData = selectedIncident ?? data;

  const fetchUsername = async () => {
    if (!data?.userid) return;
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/users/${data.userid}`,
      );
      if (!res.ok) throw new Error("User not found");
      const user = await res.json();
      setUsername(user.username || `User #${data.userid}`);
    } catch (err) {
      setUsername(`User #${data.userid}`);
    }
  };

  const fetchMessages = async () => {
    const res = await authFetch(
      `${config.apiBaseUrl}/api/messages/ticket/${data.ticketId}`,
    );
    if (res.ok) setJournalMessages(await res.json());
  };

  const fetchCommunication = async () => {
    const res = await authFetch(
      `${config.apiBaseUrl}/api/communications/ticket/${data.ticketId}`,
    );
    if (res.ok) setCommunicationMessages(await res.json());
  };

  const fetchUpdatedIncident = async (): Promise<IncidentData> => {
    const res = await authFetch(
      `${config.apiBaseUrl}/api/incidents/${data.ticketId}`,
    );
    console.log("res.json>>>", res.json);
    return await res.json();
  };
  const fetchUpdatedIncident2 = async () => {
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/incidents/${data.ticketId}`,
      );
      if (!res.ok) throw new Error("Failed to fetch updated incident");
      const updated = await res.json();
      setSelectedIncident(updated); // ✅ store in state
    } catch (err) {
      console.error("Error fetching updated incident:", err);
    }
  };
  const confirmClose = async () => {
    setShowConfirmClose(false);
    onClose();
    await new Promise((resolve) => setTimeout(resolve, 4000));
    const updatedData = await fetchUpdatedIncident();
    await handleClose(updatedData, fetchMessages);
  };

  const handlePauseWithJournal = () => {
    setPauseAfterJournal(true);
    setJournalTitle("Pause Incident (Add Journal)");
    setShowJournal(true);
  };

  const handleJournalConfirm = async () => {
    if (pauseAfterJournal) {
      await handlePause(data, fetchMessages, (updated: IncidentData) => {
        setSelectedIncident(updated); // update inside modal
        onClose(); // close modal
        onUpdate?.(updated); // ✅ optional chaining, no TS error
      });
    }
    setPauseAfterJournal(false);
    setShowJournal(false);
    onClose();
  };

  if (!isOpen) return null;

  const isAccepted = data?.isaccepted;
  const isClosed = data?.isclosed;
  const isPaused = data?.ispaused;
  return (
    <>
      {isForwardModalOpen && (
        <ForwardModal
          isOpen={isForwardModalOpen}
          data={selectedIncident}
          onClose={() => setIsForwardModalOpen(false)}
        />
      )}
      {showConfirmClose && (
        <ConfirmCloseModal
          data={data}
          onConfirm={confirmClose}
          onCancel={() => setShowConfirmClose(false)}
        />
      )}
      {showJournal && (
        <JournalModal
          title={journalTitle}
          data={data}
          isPauseJournal={pauseAfterJournal}
          onConfirm={handleJournalConfirm}
          onCancel={() => {
            setPauseAfterJournal(false);
            setShowJournal(false);
          }}
        />
      )}
      {showCommunication && (
        <CommunicationModal
          data={data}
          onConfirm={() => {
            setShowCommunication(false);
            onClose();
          }}
          onCancel={() => setShowCommunication(false)}
        />
      )}

      <div className="modal-overlay">
        <div className="modal-content">
          <div
            className="modal-top-buttons"
            style={{
              position: "sticky",
              top: -32,
              backgroundColor: "#fff",
              zIndex: 100,
              padding: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {isAdmin && (
              <button
                onClick={() =>
                  navigate(`/incident-edit/${data.ticketId}`, {
                    state: { data },
                  })
                }
              >
                Edit
              </button>
            )}

            {!isAccepted && isAdmin && (
              <button
                onClick={async () => {
                  await handleAccept(
                    data,
                    fetchMessages,
                    (updated: IncidentData) => {
                      setSelectedIncident(updated); // update inside modal
                      onClose(); // close modal
                      onUpdate?.(updated); // ✅ optional chaining, no TS error
                    },
                  );
                }}
              >
                Accept
              </button>
            )}
            {isAccepted && !isPaused && !isClosed && isAdmin && (
              <>
                <button disabled>Already Accepted</button>
                <button onClick={handlePauseWithJournal}>Pause</button>
                <button onClick={() => setShowConfirmClose(true)}>Close</button>
              </>
            )}
            {isPaused && isAdmin && !isClosed && (
              <>
                <button
                  onClick={() =>
                    handleReactivate(
                      data,
                      fetchMessages,
                      (updated: IncidentData) => {
                        setSelectedIncident(updated); // update inside modal
                        onClose(); // close modal
                        onUpdate?.(updated); // ✅ optional chaining, no TS error
                      },
                    )
                  }
                >
                  Reactivate
                </button>
                <button onClick={() => setShowConfirmClose(true)}>Close</button>
              </>
            )}
            {isClosed && isAdmin && (
              <button
                onClick={async () => {
                  await handleReopen(
                    data,
                    fetchMessages,
                    (updated: IncidentData) => {
                      setSelectedIncident(updated);
                      onUpdate?.(updated);
                    },
                  );
                  onClose(); // ✅ close ONLY after backend finishes
                }}
              >
                Reopen
              </button>
            )}
            {isAdmin && !isClosed && (
              <button
                onClick={() => {
                  setSelectedIncident(data);
                  setIsForwardModalOpen(true);
                }}
              >
                Forward
              </button>
            )}
            <button onClick={onClose}>X</button>
          </div>

          <h2
            style={{
              position: "sticky",
              top: 16, // slightly below the buttons
              backgroundColor: "#fff",
              zIndex: 99,
              margin: 0,
              padding: "10px 0",
            }}
          >
            Incident Details
          </h2>
          <div className="modal-body">
            {renderSection("Incident Information", ticketInfo, data)}
            {renderSection("Customer & Network", customerInfo, data)}
            {renderSection("Incident Severity", incInfo, data)}
            {renderSection("Incident & Details", statusInfo, incidentData)}
            {isAdmin && renderSection("Responsibility", resInfo, data)}
            {isAdmin && renderSection("SLA & OLA", slaInfo, data)}
            {renderSection("Related Object", relInfo, data)}
          </div>
          <div className="messages-container">
            {isAdmin && (
              <div className="journal">
                <button
                  onClick={() => {
                    setPauseAfterJournal(false);
                    setJournalTitle("Add Journal");
                    setShowJournal(true);
                  }}
                >
                  Add Journal
                </button>
                <h3>Journal</h3>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {[...journalMessages]
                    .sort(
                      (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime(),
                    )
                    .map((msg) => (
                      <li
                        key={msg.id}
                        style={{
                          marginBottom: "1rem",
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>
                          {fullNames[msg.username || ""] || msg.username}
                        </strong>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: msg.message.replace(/\n/g, "<br />"),
                          }}
                          style={{
                            display: "",
                            maxWidth: "100%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        />
 <small
                          style={{
                            display: "block",
                            marginTop: "4px",
                            color: "#888",
                          }}
                        >
                          {new Date(msg.date).toLocaleString()}
                        </small>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <div className="journal">
              <button onClick={() => setShowCommunication(true)}>
                Add Communication
              </button>
              <h3>Communication</h3>
               <ul style={{ listStyle: "none", padding: 0 }}>
                {[...communicationMessages]
                  .sort(
                    (a, b) =>
                      new Date(b.date).getTime() - new Date(a.date).getTime(),
                  )
                  .map((msg) => (
                    <li
                      key={msg.id}
                      style={{
                        marginBottom: "1rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {/* Display full name instead of users */}
                      <strong>{fullNames[msg.users || ""] || msg.users}</strong>
                      :&nbsp;
                      {renderSafeMessage(msg.message)}
                      
                      <br />
                      <small>{new Date(msg.date).toLocaleString()}</small>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
